import React, { FC, ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useViewIsCategoriesOfferingEnabled } from "../../../projection/offering/react/useViewIsCategoriesOfferingEnabled";
import { useViewIsCompleteYourLookOfferingEnabled } from "../../../projection/offering/react/useViewIsCompleteYourLookOfferingEnabled";
import { useViewIsSalesOfferingEnabled } from "../../../projection/offering/react/useViewIsSalesOfferingEnabled";
import { useViewIsTrendingForYouOfferingEnabled } from "../../../projection/offering/react/useViewIsTrendingForYouOfferingEnabled";
import { useGetOrder } from "../../behaviors/useGetOrder";
import { OfferingHeader as OfferingHeaderTemplate } from "../../components/templates/header/offeringHeader/OfferingHeader";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { I18nMessages } from "../../i18n/i18n";
import { Routes } from "../../routing/routes";
import { Offering as OfferingType } from "../../shared/types";
import { useTrackCategoriesSelected } from "../../tracking/useTrackCategoriesSelected";
import { useTrackCompleteYourLookSelected } from "../../tracking/useTrackCompleteYourLookSelected";

interface Offering {
  readonly enabled: boolean;
  readonly text: I18nMessages;
  readonly offering: OfferingType;
  readonly onPress: () => void;
}

interface OfferingHeaderProps {
  readonly children?: ReactNode;
  readonly onPress: () => void;
}

const OfferingHeader: FC<OfferingHeaderProps> = ({ children, onPress }) => {
  const navigate = useNavigate();

  const {
    session: { user },
    basePath,
    tradename,
  } = useStaticInfo();

  const { order = { items: [] } } = useGetOrder();
  const [isCompleteYourLookOfferingEnabled] = useViewIsCompleteYourLookOfferingEnabled();
  const [isCategoriesOfferingEnabled] = useViewIsCategoriesOfferingEnabled();
  const [isSalesOfferingEnabled] = useViewIsSalesOfferingEnabled();
  const [isTrendingForYouOfferingEnabled] = useViewIsTrendingForYouOfferingEnabled();

  const trackCategoriesSelected = useTrackCategoriesSelected({
    country: user.country,
    segment: user.segment,
    userId: user.id,
    tradename,
  });
  const trackCompleteYourLookSelected = useTrackCompleteYourLookSelected({
    country: user.country,
    segment: user.segment,
    userId: user.id,
    tradename,
  });

  const handleOnPressBasket = useCallback(() => {
    navigate(`${basePath}/${Routes.CART}`);

    onPress();
  }, [basePath, navigate, onPress]);

  const handleOnPressWishlist = useCallback(() => {
    navigate(`${basePath}/${Routes.WISHLIST}`);

    onPress();
  }, [basePath, navigate, onPress]);

  const onPressCategories = useCallback(() => {
    trackCompleteYourLookSelected({
      buttonId: "pill-categories",
    });

    trackCategoriesSelected();

    navigate(`${basePath}/${Routes.CATEGORIES}`);

    onPress();
  }, [basePath, navigate, onPress, trackCategoriesSelected, trackCompleteYourLookSelected]);

  const onPressCompleteYourLook = useCallback(() => {
    trackCompleteYourLookSelected({
      buttonId: "pill-complete-your-look",
    });

    navigate(`${basePath}/${Routes.COMPLETE_YOUR_LOOK}`);

    onPress();
  }, [basePath, navigate, onPress, trackCompleteYourLookSelected]);

  const onPressTrendingForYou = useCallback(() => {
    trackCompleteYourLookSelected({
      buttonId: "pill-trending-for-you",
    });

    navigate(`${basePath}/${Routes.TRENDING_FOR_YOU}`);

    onPress();
  }, [basePath, navigate, onPress, trackCompleteYourLookSelected]);

  const onPressSales = useCallback(() => {
    trackCompleteYourLookSelected({
      buttonId: "pill-sales",
    });

    navigate(`${basePath}/${Routes.SALES}`);

    onPress();
  }, [basePath, navigate, onPress, trackCompleteYourLookSelected]);

  const offerings: Offering[] = useMemo(
    () => [
      {
        enabled: isCompleteYourLookOfferingEnabled || false,
        text: I18nMessages.PILL_COMPLETE_YOUR_LOOK,
        offering: OfferingType.COMPLETE_YOUR_LOOK,
        onPress: onPressCompleteYourLook,
      },
      {
        enabled: isTrendingForYouOfferingEnabled || false,
        text: I18nMessages.PILL_TRENDING_FOR_YOU,
        offering: OfferingType.TRENDING_FOR_YOU,
        onPress: onPressTrendingForYou,
      },
      {
        enabled: isCategoriesOfferingEnabled || false,
        text: I18nMessages.PILL_CATEGORIES,
        offering: OfferingType.CATEGORIES,
        onPress: onPressCategories,
      },
      {
        enabled: isSalesOfferingEnabled || false,
        text: I18nMessages.PILL_SALES,
        offering: OfferingType.SALES,
        onPress: onPressSales,
      },
    ],
    [
      isCategoriesOfferingEnabled,
      isCompleteYourLookOfferingEnabled,
      isSalesOfferingEnabled,
      isTrendingForYouOfferingEnabled,
      onPressCategories,
      onPressCompleteYourLook,
      onPressSales,
      onPressTrendingForYou,
    ],
  );

  return (
    <OfferingHeaderTemplate
      hasBasketNotification={order.items.length > 0}
      offerings={offerings}
      onPressBasket={handleOnPressBasket}
      onPressWishlist={handleOnPressWishlist}
    >
      {children}
    </OfferingHeaderTemplate>
  );
};

export type { Offering };
export { OfferingHeader };
